import React, { useState, useRef, useEffect } from "react";
import "./Home.css";
import Swal from "sweetalert2";
import Footer from "../Mainpages/Footer";
import axios from "axios";

const Home = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [isRotationPaused, setIsRotationPaused] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [downloadForm, setDownloadForm] = useState({
    email: "",
    phone: "",
  });
  const [aboutContent, setAboutContent] = useState("");
  const [socialLinks, setSocialLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [rotationAngle, setRotationAngle] = useState(0);
  const rotationRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const discRef = useRef(null);

  // API Base URL
  const API_BASE_URL = "https://djv4.in/api/api";

  // Fetch data on component mount
  useEffect(() => {
    fetchSocialContent();
    fetchCategories();
  }, []);

  // Reset all states when modal is closed
  useEffect(() => {
    if (!activeSection) {
      // Reset all selections and media
      setSelectedTrack(null);
      setSelectedCategory("");
      setSelectedSubcategory("");
      setProducts([]);
      setShowDownloadForm(false);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      
      // Stop and clear any playing media
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = "";
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        videoRef.current.src = "";
      }
    }
  }, [activeSection]);

  // Fetch Social content
  const fetchSocialContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/social/`);
      const data = await response.json();
      setSocialLinks(data);
    } catch (error) {
      console.error("Error fetching social content:", error);
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/category/`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch Subcategories based on category
  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subcategory/${categoryId}/`
      );
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  // Fetch Products based on subcategory
  const fetchProducts = async (subcategoryId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product/${subcategoryId}/`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedSubcategory("");
    setProducts([]);
    if (categoryId) {
      fetchSubcategories(categoryId);
    }
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    setSelectedSubcategory(subcategoryId);
    if (subcategoryId) {
      fetchProducts(subcategoryId);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send POST request to backend
      await axios.post(`${API_BASE_URL}/contact/`, contactForm);

      // Show success notification
      Swal.fire({
        title: "Thank you!",
        text: "Your message has been sent. We will get back to you soon.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#4a4a4a",
        background: "#1a1a1a",
        color: "#fff",
      });

      // Reset form
      setContactForm({ name: "", email: "", phone: "", message: "" });
      setActiveSection(null);
    } catch (error) {
      console.error("Error submitting contact form:", error);

      // Show error notification
      Swal.fire({
        title: "Error",
        text: "There was an error submitting your message. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4a4a4a",
        background: "#1a1a1a",
        color: "#fff",
      });
    }
  };

  // Add to Cart and send download link
  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/cart/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product: selectedTrack.id,
          email: downloadForm.email,
          phone_number: downloadForm.phone,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Show success notification
        Swal.fire({
          title: "Success!",
          text: "Download request submitted successfully! You will receive the download link shortly.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#4a4a4a",
          background: "#1a1a1a",
          color: "#fff",
        });

        setApiMessage(
          "Download request submitted successfully! You will receive the download link shortly."
        );
      } else {
        // Show error notification
        Swal.fire({
          title: "Error",
          text: "Failed to submit download request. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#4a4a4a",
          background: "#1a1a1a",
          color: "#fff",
        });

        setApiMessage("Failed to submit download request. Please try again.");
      }

      setShowDownloadForm(false);
      setDownloadForm({ email: "", phone: "" });
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Show error notification
      Swal.fire({
        title: "Error",
        text: "There was an error processing your download request. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4a4a4a",
        background: "#1a1a1a",
        color: "#fff",
      });

      setApiMessage(
        "There was an error processing your download request. Please try again."
      );
    }
  };

  useEffect(() => {
    if (isRotationPaused) {
      // When paused, reset to 0 degrees and stop
      setRotationAngle(0);
      if (discRef.current) {
        discRef.current.style.transform = `rotate(0deg)`;
      }
      if (rotationRef.current) {
        cancelAnimationFrame(rotationRef.current);
      }
      return;
    }

    // When playing, start rotation from 0 degrees
    let lastTimestamp = 0;

    const rotateDisc = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;

      const elapsed = timestamp - lastTimestamp;

      if (discRef.current && elapsed > 16) {
        // ~60fps
        const newAngle = rotationAngle + 1.5;
        setRotationAngle(newAngle);
        discRef.current.style.transform = `rotate(${newAngle}deg)`;
        lastTimestamp = timestamp;
      }

      rotationRef.current = requestAnimationFrame(rotateDisc);
    };

    rotationRef.current = requestAnimationFrame(rotateDisc);

    return () => {
      if (rotationRef.current) {
        cancelAnimationFrame(rotationRef.current);
      }
    };
  }, [isRotationPaused, rotationAngle]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, [selectedTrack]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handle track changes
  useEffect(() => {
    if (selectedTrack) {
      if (selectedTrack.category_name.toLowerCase() === "audio") {
        // Handle audio track
        if (audioRef.current) {
          audioRef.current.src =
            selectedTrack.music_file || selectedTrack.music_link;
          audioRef.current.load();

          // Reset time display
          setCurrentTime(0);
          setDuration(0);

          if (isPlaying) {
            audioRef.current.play().catch((error) => {
              console.error("Error playing audio:", error);
            });
          }

          // Pause video if playing
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      } else if (selectedTrack.category_name.toLowerCase() === "video") {
        // Handle video track
        if (videoRef.current) {
          videoRef.current.src =
            selectedTrack.video_file || selectedTrack.video_link;
          videoRef.current.load();

          if (isPlaying) {
            videoRef.current.play().catch((error) => {
              console.error("Error playing video:", error);
            });
          }

          // Pause audio if playing
          if (audioRef.current) {
            audioRef.current.pause();
          }
        }
      }
    }
  }, [selectedTrack, isPlaying]);

  // Stop media playback when modal is closed
  const handleCloseModal = () => {
    // Stop and clear audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
    }
    
    // Stop and clear video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.src = "";
    }
    
    // Reset all states
    setIsPlaying(false);
    setSelectedTrack(null);
    setShowDownloadForm(false);
    setSelectedCategory("");
    setSelectedSubcategory("");
    setProducts([]);
    setCurrentTime(0);
    setDuration(0);
    setActiveSection(null);
  };

  const handleDiscHover = () => {
    setIsRotationPaused(true);
  };

  const handleDiscLeave = () => {
    setIsRotationPaused(false);
  };

  const handleQuadrantClick = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  // Handle track selection from dropdown
  const handleTrackSelect = (track) => {
    // Stop any currently playing media
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    
    // Set the new track
    setSelectedTrack(track);
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(0);
  };

  // Handle play/pause toggle
  const handlePlayToggle = () => {
    if (selectedTrack) {
      if (
        selectedTrack.category_name.toLowerCase() === "audio" &&
        audioRef.current
      ) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch((error) => {
            console.error("Error playing audio:", error);
          });
        }
      } else if (
        selectedTrack.category_name.toLowerCase() === "video" &&
        videoRef.current
      ) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play().catch((error) => {
            console.error("Error playing video:", error);
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleRotationToggle = () => {
    setIsRotationPaused(!isRotationPaused);
  };

  const handleDownloadRequest = () => {
    setShowDownloadForm(true);
  };

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    if (formType === "contact") {
      setContactForm({ ...contactForm, [name]: value });
    } else if (formType === "download") {
      setDownloadForm({ ...downloadForm, [name]: value });
    } else if (formType === "about") {
      setAboutContent(value);
    }
  };

  // Player controls without pause/resume buttons
  const renderPlayerControls = () => {
    return (
      <div className="player-controls">
        <button onClick={handleDownloadRequest}>Download</button>
      </div>
    );
  };

  return (
    <>
      <div className="record-player-container">
        <div className="background"></div>

        <div
          className="disc-container"
          onMouseEnter={handleDiscHover}
          onMouseLeave={handleDiscLeave}
        >
          <div className="disc" ref={discRef}>
            <div className="disc-inner">
              <div
                className={`quadrant quadrant-about ${
                  activeSection === "about" ? "active" : ""
                }`}
                onClick={() => handleQuadrantClick("about")}
              >
                <span>ABOUT</span>
              </div>
              <div
                className={`quadrant quadrant-samples ${
                  activeSection === "samples" ? "active" : ""
                }`}
                onClick={() => handleQuadrantClick("samples")}
              >
                <span>SAMPLES</span>
              </div>
              <div
                className={`quadrant quadrant-social ${
                  activeSection === "social" ? "active" : ""
                }`}
                onClick={() => handleQuadrantClick("social")}
              >
                <span>SOCIAL</span>
              </div>
              <div
                className={`quadrant quadrant-contact ${
                  activeSection === "contact" ? "active" : ""
                }`}
                onClick={() => handleQuadrantClick("contact")}
              >
                <span>CONTACT</span>
              </div>
              <div
                className="disc-center"
                onMouseEnter={handleDiscHover}
                onMouseLeave={handleDiscLeave}
              >
                <img
                  src="../../assets/images/logo.png"
                  alt="DJV4 Logo"
                  className="center-logo"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="control-buttons">
          <button
            className={`rotation-button ${isRotationPaused ? "paused" : ""}`}
            onClick={handleRotationToggle}
          >
            {isRotationPaused ? "▶ Rotation" : "❚❚ Rotation"}
          </button>
        </div>

        {/* Audio element (hidden) */}
        <audio ref={audioRef}>
          Your browser does not support the audio element.
        </audio>

        {/* Video element (hidden but used for playback control) */}
        <video ref={videoRef} style={{ display: "none" }}>
          Your browser does not support the video element.
        </video>

        {/* Section overlays */}
        {activeSection && (
          <div className="overlay">
            <button className="close-button" onClick={handleCloseModal}>
              ×
            </button>

            {activeSection === "about" && (
              <div className="content about-content">
                <h2>ABOUT DJV4</h2>
                <p>
                  Welcome to DJV4 - your premier destination for high-quality
                  music and video samples.
                </p>
                <p>
                  Explore our collection, connect with us on social media, or
                  get in touch for custom requests.
                </p>
              </div>
            )}

            {activeSection === "samples" && (
              <div className="content samples-content">
                <h2>LISTEN & WATCH</h2>

                {/* Audio Player */}
                {selectedTrack &&
                  selectedTrack.category_name.toLowerCase() === "audio" && (
                    <div className="visualizer-container">
                      {/* If direct MP3 file or MP3 link → play with <audio> */}
                      {selectedTrack.music_file ||
                      (selectedTrack.music_link &&
                        selectedTrack.music_link.endsWith(".mp3")) ? (
                        <>
                          <audio
                            ref={audioRef}
                            src={
                              selectedTrack.music_file ||
                              selectedTrack.music_link
                            }
                            controls
                            autoPlay={isPlaying}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            style={{ display: "none" }}
                          />

                          {/* Visualizer */}
                          <div
                            className={`visualizer ${
                              isPlaying ? "active" : ""
                            }`}
                          >
                            {Array.from({ length: 36 }).map((_, i) => (
                              <div key={i} className="bar"></div>
                            ))}
                          </div>

                          {/* Time display */}
                          <div className="time-display">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>

                          {/* Play/Pause button */}
                          <button
                            className={`play-button ${
                              isPlaying ? "playing" : ""
                            }`}
                            onClick={handlePlayToggle}
                            disabled={!selectedTrack}
                          >
                            <span>{isPlaying ? "❚❚" : "▶"}</span>
                          </button>
                        </>
                      ) : (
                        // Else → show embed iframe (for JioSaavn, Spotify, etc.)
                        <iframe
                          src={selectedTrack.music_link}
                          width="100%"
                          height="80"
                          frameBorder="0"
                          allow="autoplay; clipboard-write; encrypted-media"
                          allowFullScreen
                          title="Music player"
                          style={{ marginBottom: "10px", borderRadius: "8px" }}
                        ></iframe>
                      )}
                    </div>
                  )}

                {/* Video Player */}
                {selectedTrack &&
                  selectedTrack.category_name.toLowerCase() === "video" && (
                    <div className="video-player-container">
                      {selectedTrack.video_file ? (
                        // Case 1: Direct video file
                        <video
                          ref={videoRef}
                          controls
                          autoPlay={isPlaying}
                          style={{
                            width: "100%",
                            maxHeight: "300px",
                            marginBottom: "15px",
                          }}
                          controlsList="nodownload"
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                          onEnded={() => setIsPlaying(false)}
                        >
                          <source
                            src={selectedTrack.video_file}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        // Case 2: YouTube embed link
                        <iframe
                          width="100%"
                          height="300"
                          src={selectedTrack.video_link}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ marginBottom: "15px" }}
                        ></iframe>
                      )}

                      {/* Play/Pause button only for file videos */}
                      {selectedTrack.video_file && (
                        <button
                          className={`play-button ${
                            isPlaying ? "playing" : ""
                          }`}
                          onClick={handlePlayToggle}
                          disabled={!selectedTrack}
                          style={{ marginTop: "10px" }}
                        >
                          <span>{isPlaying ? "❚❚" : "▶"}</span>
                        </button>
                      )}
                    </div>
                  )}

                <div className="media-selector">
                  {/* Category selection as cards/buttons */}
                  <div className="category-cards">
                    <h3>Select Category</h3>
                    <div className="cards-container">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className={`category-card ${
                            selectedCategory === category.id ? "selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            setSelectedSubcategory("");
                            setProducts([]);
                            fetchSubcategories(category.id);
                          }}
                        >
                          {category.category_name}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Subcategory dropdown */}
                  {selectedCategory && (
                    <select
                      value={selectedSubcategory}
                      onChange={handleSubcategoryChange}
                      style={{ marginTop: "10px", width: "100%" }}
                    >
                      <option value="">Select Subcategory</option>
                      {subcategories.map((subcategory) => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.subcategory_name}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Track list */}
                  {selectedSubcategory && (
                    <div className="track-list" style={{ marginTop: "15px" }}>
                      <h3>Available Tracks</h3>
                      <select
                        className="track-dropdown"
                        value={selectedTrack?.id || ""}
                        onChange={(e) => {
                          const track = products.find(
                            (p) => p.id === parseInt(e.target.value)
                          );
                          if (track) {
                            handleTrackSelect(track);
                          }
                        }}
                      >
                        <option value="">-- Select a track --</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.category_name.toLowerCase() === "audio"
                              ? "🎵"
                              : "🎬"}{" "}
                            {product.product_name} - {product.author_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Player container */}
                  {selectedTrack && (
                    <div className="player-container">
                      <h3>
                        Now{" "}
                        {selectedTrack.category_name.toLowerCase() === "audio"
                          ? "Playing"
                          : "Watching"}
                        : {selectedTrack.product_name}
                      </h3>

                      {showDownloadForm ? (
                        <form
                          className="download-form"
                          onSubmit={handleDownloadSubmit}
                        >
                          <div className="download-form-header">
                            <p>
                              Provide your email or phone to receive download
                              link
                            </p>
                            <button
                              type="button"
                              className="close-download-form"
                              onClick={() => setShowDownloadForm(false)}
                              aria-label="Close download form"
                            >
                              ×
                            </button>
                          </div>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email address"
                            value={downloadForm.email}
                            onChange={(e) => handleInputChange(e, "download")}
                            required
                          />
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Phone number"
                            value={downloadForm.phone}
                            onChange={(e) => handleInputChange(e, "download")}
                            required
                          />
                          <div className="player-controls">
                            <button type="submit">Send Download Link</button>
                          </div>
                        </form>
                      ) : (
                        renderPlayerControls()
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === "social" && (
              <div className="content social-content">
                <h2>CONNECT WITH US</h2>
                <div className="social-icons">
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.text}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-icon"
                    >
                      {link.name.charAt(0).toUpperCase() + link.name.slice(1)}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {activeSection === "contact" && (
              <div className="content contact-content">
                <h2>GET IN TOUCH</h2>
                <form onSubmit={handleContactSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={contactForm.name}
                    onChange={(e) => handleInputChange(e, "contact")}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={contactForm.email}
                    onChange={(e) => handleInputChange(e, "contact")}
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone (optional)"
                    value={contactForm.phone}
                    onChange={(e) => handleInputChange(e, "contact")}
                  />
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={contactForm.message}
                    onChange={(e) => handleInputChange(e, "contact")}
                    required
                  ></textarea>
                  <button type="submit">Send Message</button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Home;